/**
 * Energy Mode Copy Utility (Phase 7)
 * ==================================
 *
 * Display copy for the five Auto-Mom energy modes and the day's capacity.
 *
 * Core Principle: CAPACITY FRAMING, not WORTH FRAMING
 * - Worth:    "You only did 2 of 5 — you're behind / unproductive."
 * - Capacity: "Today is a Recovery day. Protecting your energy is the win."
 *
 * Language rules:
 * - The UI shows Core / Optional / Available time / Energy mode — NOT a
 *   "units completed" score (units appear ONLY in an explanation view, never
 *   as a productivity-quota scoreboard).
 * - A mode names today's CAPACITY, never the user's worth. Superhero is a
 *   temporary moment that auto-expires — never a permanent baseline label.
 * - DO NOT use: behind, failure/failed, lazy, unproductive/productivity, quota,
 *   score, "should have", "didn't finish", "%".
 * - DO use: protect, sustainable, gently, room, optional, enough, your call.
 */

export type EnergyMode = 'recovery' | 'gentle' | 'average' | 'momentum' | 'superhero'

export interface EnergyModeDisplay {
  mode: EnergyMode
  /** Short capacity label, e.g. "Protect energy". */
  label: string
  /** One-line capacity (never worth) description. */
  tagline: string
  /** True only for modes that auto-expire and must never read as a permanent baseline. */
  isTemporary: boolean
  /** Always 'capacity' — a compile-time reminder that copy frames capacity, not worth. */
  framing: 'capacity'
}

const ENERGY_MODE_DISPLAY: Record<EnergyMode, EnergyModeDisplay> = {
  recovery: {
    mode: 'recovery',
    label: 'Protect energy',
    tagline: 'A recovery day — going gently today is what protects tomorrow.',
    isTemporary: false,
    framing: 'capacity',
  },
  gentle: {
    mode: 'gentle',
    label: 'Light progress',
    tagline: 'A light day. One small step forward is plenty.',
    isTemporary: false,
    framing: 'capacity',
  },
  average: {
    mode: 'average',
    label: 'Sustainable day',
    tagline: 'A steady, sustainable pace — the kind you can repeat.',
    isTemporary: false,
    framing: 'capacity',
  },
  momentum: {
    mode: 'momentum',
    label: 'Extra room',
    tagline: "There's a little extra room today, if you feel like using it.",
    isTemporary: false,
    framing: 'capacity',
  },
  superhero: {
    mode: 'superhero',
    label: 'Strong energy, optional stretch',
    tagline: 'Strong energy today — room for an optional stretch. A good moment, not a new normal.',
    isTemporary: true, // auto-expires; never a permanent baseline label
    framing: 'capacity',
  },
}

const DEFAULT_MODE: EnergyMode = 'average'
export const ENERGY_MODES: EnergyMode[] = [
  'recovery', 'gentle', 'average', 'momentum', 'superhero',
]

export function isEnergyMode(value: string | null | undefined): value is EnergyMode {
  return value != null && Object.prototype.hasOwnProperty.call(ENERGY_MODE_DISPLAY, value)
}

/** Display copy for a mode. Falls back to the sustainable Average framing for an
 * unknown/absent mode (never throws, never a "worth" judgement). */
export function getEnergyModeDisplay(mode: string | null | undefined): EnergyModeDisplay {
  return ENERGY_MODE_DISPLAY[isEnergyMode(mode) ? mode : DEFAULT_MODE]
}

// ---------------------------------------------------------------------------
// Core / Optional / Available-time surfacing (units stay in an explanation view)
// ---------------------------------------------------------------------------

export interface CapacityState {
  /** Number of committed CORE items today. */
  core: number
  /** Number of OPTIONAL items on offer (never required). */
  optional: number
  /** Free calendar minutes available today. */
  availableMinutes: number
  /** Whether the committed core is already complete. */
  coreComplete?: boolean
}

export interface CapacitySummary {
  coreLabel: string
  optionalLabel: string
  availableLabel: string
}

function formatMinutes(minutes: number): string {
  // Guard non-finite input BEFORE clamping: Math.max(0, NaN) is NaN, which would
  // otherwise render a literal "NaN hr NaN min" on the primary capacity surface.
  const safe = Number.isFinite(minutes) ? minutes : 0
  const m = Math.max(0, Math.round(safe))
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const rem = m % 60
  return rem === 0 ? `${h} hr` : `${h} hr ${rem} min`
}

/** Primary surfacing: Core / Optional / Available time — no units, no score. */
export function summarizeCapacity(state: CapacityState): CapacitySummary {
  return {
    coreLabel: state.coreComplete
      ? 'Core done'
      : `${state.core} core ${state.core === 1 ? 'thing' : 'things'}`,
    optionalLabel: state.optional > 0
      ? `${state.optional} optional`
      : 'No extras needed',
    availableLabel: `${formatMinutes(state.availableMinutes)} free`,
  }
}

/** Units are an EXPLANATION-only detail (e.g. behind a "why?" disclosure),
 * never part of the primary capacity surface. */
export function explainUnits(args: {
  releasedUnits: number
  committedCeiling: number
  optionalCeiling?: number
}): string {
  const { releasedUnits, committedCeiling, optionalCeiling } = args
  const base = `Today's plan weighs about ${releasedUnits} of ${committedCeiling} core units`
  return optionalCeiling && optionalCeiling > 0
    ? `${base}, with up to ${optionalCeiling} more available as optional.`
    : `${base}.`
}

// ---------------------------------------------------------------------------
// Permission-to-stop card (fires on core_complete, independent of optional)
// ---------------------------------------------------------------------------

export interface PermissionToStopCard {
  title: string
  body: string
  primaryLabel: string
  secondaryLabel?: string
}

/** The core-complete celebration card. Acknowledges the win and grants
 * permission to stop FIRST; an optional offer is secondary and never implied
 * to be required. Fires even when there are zero optional candidates. */
export function permissionToStopCard(opts: { hasOptional: boolean }): PermissionToStopCard {
  if (opts.hasOptional) {
    return {
      title: "You finished today's core",
      body: "That's a real win. You can stop here and enjoy it, or pick up one optional thing if you feel like it — both are good choices.",
      primaryLabel: 'Stop for today',
      secondaryLabel: 'See one optional thing',
    }
  }
  return {
    title: "You finished today's core",
    body: "That's a real win. You're free to stop and enjoy it — there's nothing else you need to do today.",
    primaryLabel: 'Stop for today',
  }
}

// ---------------------------------------------------------------------------
// Doctrine guard: copy must frame capacity, never worth
// ---------------------------------------------------------------------------

const FORBIDDEN_WORTH_LANGUAGE: string[] = [
  'behind', 'failure', 'failed', 'lazy', 'unproductive', 'productive',
  'productivity', 'quota', 'score', 'should have', "didn't finish",
  'did not finish', 'incomplete', 'slacking', 'not enough', '%',
]

export interface CopyValidation {
  valid: boolean
  violations: string[]
}

/** True when a forbidden term appears in ``haystack`` on alphabetic word
 * boundaries — so "score" matches "high score" but NOT "underscore", and
 * "productive" does not bite "unproductive" (its own distinct entry). ``%`` is a
 * literal substring check (word boundaries don't apply to it). */
function isForbiddenPresent(haystack: string, term: string): boolean {
  if (term === '%') return haystack.includes('%')
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`(^|[^a-z])${escaped}([^a-z]|$)`, 'i').test(haystack)
}

/** Scan one or more strings for worth/productivity-quota language. Returns the
 * specific violations so a test (or CI) can pinpoint the offending phrase. */
export function validateEnergyModeCopy(...texts: Array<string | undefined | null>): CopyValidation {
  const haystack = texts.filter(Boolean).join(' ').toLowerCase()
  const violations = FORBIDDEN_WORTH_LANGUAGE.filter((w) => isForbiddenPresent(haystack, w))
  return { valid: violations.length === 0, violations }
}
